import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowRight, RotateCcw, Sparkles, Eye, Check, Save, LogIn, User } from 'lucide-react';
import { toast } from 'sonner';

// ── Questions ──

const QUESTIONS = [
  {
    id: 'interest', title: 'What interests you most?', type: 'single',
    options: ['Helping people', 'Technology', 'Business', 'Creativity', 'Sports & fitness', 'Science & research', 'Teaching', 'Travel & hospitality'],
  },
  {
    id: 'subjects', title: 'Which subjects do you enjoy?', type: 'multi',
    options: ['Accountancy', 'Applied Mathematics', 'Artificial Intelligence', 'Biology', 'Business Studies', 'Chemistry', 'Commercial Art', 'Computer Science', 'Economics', 'Entrepreneurship', 'Geography', 'History', 'Legal Studies', 'Mathematics', 'Media Studies', 'Physical Education', 'Physics', 'Political Science', 'Psychology', 'Not sure'],
  },
  {
    id: 'workType', title: 'What kind of work excites you?', type: 'single',
    options: ['Solving problems', 'Creating things', 'Leading people', 'Researching', 'Performing', 'Training others', 'Working with tools', 'Working with people'],
  },
  {
    id: 'environment', title: 'Where would you like to work?', type: 'single',
    options: ['Office', 'Hospital', 'Creative studio', 'Outdoors / travel', 'Lab / research', 'Sports / fitness', 'Flexible / mixed', 'Not sure'],
  },
  {
    id: 'journey', title: 'What kind of path suits you?', type: 'single',
    options: ['Structured study', 'Study + practical', 'Skill / portfolio', 'Flexible / explore', 'Not sure'],
  },
  {
    id: 'personality', title: 'What describes you best?', type: 'single',
    options: ['Analytical', 'Creative', 'Caring', 'Leader', 'Curious', 'Disciplined', 'Energetic', 'Social'],
  },
];

const MICRO_RESPONSES = ['Got it!', 'Nice!', 'That helps!', 'Great choice!', 'Interesting!', 'Good to know!'];

// ── Matching Maps ──

const INTEREST_CATEGORIES = {
  'Helping people': ['Science & Healthcare', 'Education & Coaching', 'Law, Government & Social Impact'],
  'Technology': ['Technology'],
  'Business': ['Business & Management', 'Commerce & Finance'],
  'Creativity': ['Media, Arts & Design'],
  'Sports & fitness': ['Sports & Fitness'],
  'Science & research': ['Science & Healthcare', 'Engineering & Architecture'],
  'Teaching': ['Education & Coaching'],
  'Travel & hospitality': ['Hospitality, Travel & Lifestyle'],
};

const WORK_TYPE_KEYWORDS = {
  'Solving problems': ['problem-solving', 'logical', 'analytical', 'debugging'],
  'Creating things': ['creativity', 'building', 'expression', 'storytelling', 'visual'],
  'Leading people': ['leadership', 'strategy', 'communication', 'decision-making'],
  'Researching': ['science', 'observation', 'careful study', 'research'],
  'Performing': ['creativity', 'expression', 'storytelling', 'performance'],
  'Training others': ['teaching', 'mentoring', 'listening', 'helping others'],
  'Working with tools': ['digital tools', 'systems', 'technical', 'problem-solving'],
  'Working with people': ['people-facing', 'service', 'empathy', 'listening'],
};

const ENV_KEYWORDS = {
  'Office': ['corporate', 'offices', 'consulting', 'banks'],
  'Hospital': ['hospital', 'clinic', 'healthcare', 'diagnostic', 'dental', 'pharma', 'rehab'],
  'Creative studio': ['studios', 'agencies', 'media teams', 'production'],
  'Outdoors / travel': ['field', 'outdoor', 'travel', 'airlines', 'aviation'],
  'Lab / research': ['labs', 'research centers', 'pharma'],
  'Sports / fitness': ['sports academies', 'gyms', 'training centers'],
  'Flexible / mixed': ['mix of office', 'flexible'],
};

const JOURNEY_PREP = {
  'Structured study': 'Highly structured and often competitive',
  'Study + practical': 'Structured path with skill-building and practice',
  'Skill / portfolio': 'Flexible or portfolio/skill-based entry path',
};

const PERSONALITY_KEYWORDS = {
  'Analytical': ['problem-solving', 'logical', 'analytical', 'numbers', 'patterns'],
  'Creative': ['creativity', 'expression', 'storytelling', 'visual'],
  'Caring': ['empathy', 'listening', 'helping', 'patient care', 'supportive'],
  'Leader': ['leadership', 'strategy', 'communication', 'ambitious'],
  'Curious': ['learning by doing', 'continuous learning', 'experimental', 'exploring'],
  'Disciplined': ['discipline', 'structured', 'focus', 'precision'],
  'Energetic': ['movement', 'training', 'competition', 'action', 'performance'],
  'Social': ['communication', 'people-facing', 'service', 'social'],
};

const SUBJECT_MAP = { 'Not sure': null };
const W = { interest: 3, subjects: 2, workType: 3, environment: 2, journey: 1, personality: 1 };

// ── Improved Reason Generation ──

const INTEREST_REASON = {
  'Helping people': 'helping people',
  'Technology': 'technology',
  'Business': 'business and strategy',
  'Creativity': 'creativity',
  'Sports & fitness': 'sports and fitness',
  'Science & research': 'science and research',
  'Teaching': 'teaching and guiding',
  'Travel & hospitality': 'travel and hospitality',
};

const WORK_REASON = {
  'Solving problems': 'problem-solving',
  'Creating things': 'creative work',
  'Leading people': 'leadership',
  'Researching': 'research and discovery',
  'Performing': 'performance and expression',
  'Training others': 'training and mentoring',
  'Working with tools': 'systems and tools',
  'Working with people': 'working with people',
};

const ENV_REASON = {
  'Office': 'office environments',
  'Hospital': 'healthcare settings',
  'Creative studio': 'creative spaces',
  'Outdoors / travel': 'outdoor and travel settings',
  'Lab / research': 'lab and research settings',
  'Sports / fitness': 'active environments',
  'Flexible / mixed': 'flexible work settings',
};

function scoreCareer(career, answers) {
  let score = 0;
  const reasonParts = { interest: null, subjects: null, workType: null, environment: null };

  if (answers.interest) {
    const cats = INTEREST_CATEGORIES[answers.interest] || [];
    if (cats.includes(career.category)) {
      score += W.interest;
      reasonParts.interest = INTEREST_REASON[answers.interest];
    }
  }

  if (answers.subjects && answers.subjects.length > 0 && !answers.subjects.includes('Not sure')) {
    const careerSubjects = (career.subjects || []).map(s => s.toLowerCase());
    const mapped = answers.subjects.map(s => (SUBJECT_MAP[s] || s).toLowerCase());
    const matched = mapped.filter(s => careerSubjects.includes(s));
    if (matched.length > 0) {
      score += W.subjects * (matched.length / mapped.length);
      const displayNames = matched.map(m => {
        const orig = answers.subjects.find(s => (SUBJECT_MAP[s] || s).toLowerCase() === m);
        return orig || m;
      });
      reasonParts.subjects = displayNames.slice(0, 2).join(' & ');
    }
  }

  if (answers.workType) {
    const keywords = WORK_TYPE_KEYWORDS[answers.workType] || [];
    const combined = ((career.best_fit_for || '') + ' ' + (career.work_environment || '')).toLowerCase();
    if (keywords.some(k => combined.includes(k))) {
      score += W.workType * Math.min(1, keywords.filter(k => combined.includes(k)).length / 2);
      reasonParts.workType = WORK_REASON[answers.workType];
    }
  }

  if (answers.environment && answers.environment !== 'Not sure') {
    const keywords = ENV_KEYWORDS[answers.environment] || [];
    const envText = (career.work_environment || '').toLowerCase();
    if (keywords.some(k => envText.includes(k))) {
      score += W.environment * Math.min(1, keywords.filter(k => envText.includes(k)).length / 2);
      reasonParts.environment = ENV_REASON[answers.environment];
    }
  }

  if (answers.journey && answers.journey !== 'Not sure' && answers.journey !== 'Flexible / explore') {
    const targetPrep = JOURNEY_PREP[answers.journey];
    if (targetPrep && career.preparation_style === targetPrep) score += W.journey;
  }

  if (answers.personality) {
    const keywords = PERSONALITY_KEYWORDS[answers.personality] || [];
    const fitText = (career.best_fit_for || '').toLowerCase();
    if (keywords.some(k => fitText.includes(k))) {
      score += W.personality * Math.min(1, keywords.filter(k => fitText.includes(k)).length / 2);
    }
  }

  // Build concise reasons (max 3, under 10 words each)
  const reasons = [];
  if (reasonParts.interest && reasonParts.workType) {
    reasons.push(`Matches your interest in ${reasonParts.interest}`);
    reasons.push(`Great for ${reasonParts.workType}`);
  } else if (reasonParts.interest) {
    reasons.push(`Matches your interest in ${reasonParts.interest}`);
  } else if (reasonParts.workType) {
    reasons.push(`Great for ${reasonParts.workType}`);
  }
  if (reasonParts.subjects) {
    reasons.push(`Aligned with ${reasonParts.subjects}`);
  }
  if (reasonParts.environment && reasons.length < 3) {
    reasons.push(`Fits ${reasonParts.environment}`);
  }
  if (reasons.length === 0 && score > 0) {
    reasons.push('Matches your overall profile');
  }

  return { career, score, reasons: reasons.slice(0, 3) };
}

// ── Session Storage Helpers ──

const STORAGE_KEY = 'career_buddy_pending';

function savePendingState(answers, results) {
  try {
    const data = {
      answers,
      results: results.map(r => ({
        slug: r.career.slug,
        name: r.career.name,
        short_description: r.career.short_description,
        reasons: r.reasons,
        category: r.career.category,
      })),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
}

export function getPendingBuddyState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

export function clearPendingBuddyState() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

// ── Chat UI Components ──

function BotBubble({ children, animate }) {
  return (
    <div className={`flex gap-2 mb-3 ${animate ? 'animate-in fade-in slide-in-from-bottom-2 duration-300' : ''}`}>
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#E85D4A] flex items-center justify-center shrink-0 mt-0.5">
        <Sparkles className="h-3 w-3 text-white" />
      </div>
      <div className="bg-[#F4F5F7] rounded-2xl rounded-tl-md px-3.5 py-2.5 max-w-[85%]">
        {children}
      </div>
    </div>
  );
}

function UserBubble({ children, animate }) {
  return (
    <div className={`flex justify-end mb-3 ${animate ? 'animate-in fade-in slide-in-from-bottom-2 duration-300' : ''}`}>
      <div className="bg-[#F97D6A] text-white rounded-2xl rounded-tr-md px-3.5 py-2 max-w-[85%]">
        {children}
      </div>
    </div>
  );
}

function Chip({ label, selected, onClick, testId, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      data-testid={testId}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-xl text-[12px] font-medium transition-all duration-150 text-left
        ${disabled
          ? 'bg-gray-50 border border-gray-100 text-gray-300 cursor-not-allowed'
          : selected
            ? 'bg-[#F97D6A] text-white shadow-sm scale-[1.02]'
            : 'bg-white border border-gray-200 text-[#374151] hover:border-[#F97D6A]/40 hover:bg-[#FFF7F5]'
        }`}
    >
      <span className="flex items-center gap-1">
        {selected && <Check className="h-2.5 w-2.5 shrink-0" />}
        {label}
      </span>
    </button>
  );
}

function ActiveQuestion({ question, stepIndex, totalSteps, pendingAnswer, onPendingChange, onConfirm }) {
  const isMulti = question.type === 'multi';
  const current = isMulti ? (pendingAnswer || []) : pendingAnswer;
  const canConfirm = isMulti ? current.length > 0 : !!current;

  const EXCLUSIVE_PAIRS = {
    'Mathematics': 'Applied Mathematics',
    'Applied Mathematics': 'Mathematics',
    'Computer Science': 'Artificial Intelligence',
    'Artificial Intelligence': 'Computer Science',
  };

  const handleChip = (option) => {
    if (isMulti) {
      if (option === 'Not sure') { onPendingChange(['Not sure']); return; }
      let arr = (current || []).filter(a => a !== 'Not sure');
      if (arr.includes(option)) {
        onPendingChange(arr.filter(o => o !== option));
      } else {
        // Remove mutually exclusive option if selecting subjects
        if (question.id === 'subjects') {
          const exclusive = EXCLUSIVE_PAIRS[option];
          if (exclusive) arr = arr.filter(o => o !== exclusive);
        }
        onPendingChange([...arr, option]);
      }
    } else {
      onPendingChange(option);
    }
  };

  const isOptionDisabled = (option) => {
    if (!isMulti || question.id !== 'subjects') return false;
    const exclusive = EXCLUSIVE_PAIRS[option];
    return exclusive ? (current || []).includes(exclusive) : false;
  };

  return (
    <div data-testid={`career-buddy-step-${stepIndex + 1}`}>
      <div className="pl-8 mb-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-semibold text-[#F97D6A]">Question {stepIndex + 1}/{totalSteps}</span>
          {isMulti && <span className="text-[9px] text-[#9CA3AF] bg-gray-100 px-1.5 py-0.5 rounded-full">Pick multiple</span>}
        </div>
        <Progress value={((stepIndex + 1) / totalSteps) * 100} className="h-1 bg-gray-100 [&>div]:bg-[#F97D6A]" />
      </div>
      <div className="pl-8 grid grid-cols-2 gap-1.5 mb-3">
        {question.options.map((opt) => (
          <Chip
            key={opt}
            label={opt}
            selected={isMulti ? (current || []).includes(opt) : current === opt}
            disabled={isOptionDisabled(opt)}
            onClick={() => handleChip(opt)}
            testId={`buddy-option-${opt.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
          />
        ))}
      </div>
      <div className="pl-8 flex justify-end">
        <Button
          onClick={onConfirm}
          disabled={!canConfirm}
          className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-8 px-4 text-xs font-semibold gap-1 disabled:opacity-40"
          data-testid="career-buddy-next-btn"
        >
          {stepIndex === totalSteps - 1 ? 'See my matches' : 'Continue'}
          <ArrowRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function ResultsSection({ results, onViewCareer, onSave, onReset, saving, saved }) {
  const top3 = results.slice(0, 3);
  const also3 = results.slice(3, 6);

  const CareerCard = ({ r, i, highlight }) => (
    <div
      onClick={() => onViewCareer(r.career)}
      className={`group cursor-pointer rounded-xl p-3 transition-all duration-200 hover:shadow-sm ${highlight ? 'bg-white border border-[#F97D6A]/20 shadow-sm' : 'bg-white border border-gray-150'}`}
      data-testid={`buddy-result-${r.career.slug}`}
    >
      <div className="flex items-start gap-2">
        <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 text-[10px] font-bold ${highlight ? 'bg-[#FFF0ED] text-[#F97D6A]' : 'bg-gray-100 text-gray-500'}`}>
          {i + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-0.5">
            <h4 className="font-semibold text-[12px] text-[#1F2937] font-['Outfit'] group-hover:text-[#F97D6A] transition-colors truncate">
              {r.career.name}
            </h4>
            <Eye className="h-3 w-3 text-gray-300 group-hover:text-[#F97D6A] shrink-0" />
          </div>
          <p className="text-[10px] text-[#6B7280] line-clamp-1 mb-1">{r.career.short_description}</p>
          <div className="flex flex-wrap gap-1">
            {r.reasons.map((reason, ri) => (
              <span key={ri} className="text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full leading-tight">
                {reason}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="career-buddy-results">
      <BotBubble animate>
        <p className="text-[13px] text-[#374151]">
          Here are your top matches! I've grouped them into your best fits and some more you might like.
        </p>
      </BotBubble>

      {/* Best Matches */}
      <div className="pl-8 mb-3">
        <p className="text-[10px] font-bold text-[#F97D6A] uppercase tracking-wider mb-1.5">Best matches for you</p>
        <div className="space-y-1.5">
          {top3.map((r, i) => <CareerCard key={r.career.slug} r={r} i={i} highlight />)}
        </div>
      </div>

      {/* Also Like */}
      {also3.length > 0 && (
        <div className="pl-8 mb-3">
          <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-1.5">You might also like</p>
          <div className="space-y-1.5">
            {also3.map((r, i) => <CareerCard key={r.career.slug} r={r} i={i + 3} />)}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="pl-8 flex flex-col gap-1.5 mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => onViewCareer(null)}
            className="flex-1 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-9 text-xs font-semibold"
            data-testid="career-buddy-explore-btn"
          >
            Show on Explore Page
          </Button>
          <Button
            variant="outline"
            onClick={onSave}
            disabled={saving || saved}
            className={`flex-1 rounded-xl h-9 text-xs gap-1 ${saved ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : ''}`}
            data-testid="career-buddy-save-btn"
          >
            {saved ? <><Check className="h-3 w-3" /> Saved</> : saving ? 'Saving...' : <><Save className="h-3 w-3" /> Save My Results</>}
          </Button>
        </div>
        <Button variant="ghost" onClick={onReset} className="rounded-xl h-7 text-[11px] text-[#9CA3AF] gap-1" data-testid="career-buddy-reset-btn">
          <RotateCcw className="h-3 w-3" /> Start Over
        </Button>
      </div>
    </div>
  );
}

function SavePrompt({ type, onLogin, onClose }) {
  return (
    <div className="text-center py-3" data-testid="career-buddy-save-prompt">
      <div className="w-11 h-11 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-3">
        {type === 'login' ? <LogIn className="h-5 w-5 text-[#F97D6A]" /> : <User className="h-5 w-5 text-[#F97D6A]" />}
      </div>
      <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-1">
        {type === 'login' ? 'Save your matches' : 'No children linked'}
      </h3>
      <p className="text-[11px] text-[#6B7280] mb-4 max-w-xs mx-auto leading-relaxed">
        {type === 'login'
          ? 'Log in to save your career matches and view them on your dashboard.'
          : 'Add a child to your profile first to save career match results.'}
      </p>
      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" onClick={onClose} className="rounded-xl h-8 px-4 text-xs">Maybe Later</Button>
        <Button onClick={onLogin} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-8 px-5 text-xs font-semibold gap-1.5" data-testid="career-buddy-login-btn">
          {type === 'login' ? <><LogIn className="h-3 w-3" /> Log in</> : <><User className="h-3 w-3" /> Go to Dashboard</>}
        </Button>
      </div>
    </div>
  );
}

function ChildPickerPrompt({ children, selectedChildId, onSelect, onConfirm, onClose, saving }) {
  return (
    <div className="text-center py-3" data-testid="career-buddy-child-picker">
      <div className="w-11 h-11 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-3">
        <Save className="h-5 w-5 text-[#F97D6A]" />
      </div>
      <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-1">Save for which child?</h3>
      <p className="text-[11px] text-[#6B7280] mb-3">Select the child to save these results for.</p>
      <div className="max-w-xs mx-auto mb-4">
        <Select value={selectedChildId} onValueChange={onSelect}>
          <SelectTrigger className="h-9 rounded-xl border-gray-200 text-sm" data-testid="career-buddy-child-select">
            <SelectValue placeholder="Select child" />
          </SelectTrigger>
          <SelectContent>
            {children.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.full_name} — Grade {c.grade}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" onClick={onClose} className="rounded-xl h-8 px-4 text-xs">Cancel</Button>
        <Button
          onClick={onConfirm}
          disabled={!selectedChildId || saving}
          className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-8 px-5 text-xs font-semibold gap-1.5 disabled:opacity-40"
          data-testid="career-buddy-save-confirm-btn"
        >
          <Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save Results'}
        </Button>
      </div>
    </div>
  );
}

// ── Main Modal ──

export function CareerBuddyModal({ open, onOpenChange, onMatchComplete, restoreMode }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scrollRef = useRef(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentQIdx, setCurrentQIdx] = useState(-1); // -1 = intro, 0..5 = questions, 6 = done
  const [answers, setAnswers] = useState({});
  const [pendingAnswer, setPendingAnswer] = useState(null);
  const [phase, setPhase] = useState('chat'); // chat | results | save-prompt
  const [allCareers, setAllCareers] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savePhase, setSavePhase] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');

  // Auto-scroll
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  }, []);

  // Fetch careers on open
  useEffect(() => {
    if (open && allCareers.length === 0) {
      setLoading(true);
      api.get('/careers?limit=100')
        .then(res => setAllCareers(res.data.careers || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, allCareers.length]);

  // Initialize or restore on open
  useEffect(() => {
    if (!open) return;

    // Check for pending restore from sessionStorage (after login redirect)
    if (restoreMode) {
      const pending = getPendingBuddyState();
      if (pending && pending.answers && pending.results) {
        setAnswers(pending.answers);
        // Build message history from answers
        const msgs = [{ type: 'bot', text: "Hi! I'll ask you a few quick questions to help you discover careers that may suit you." }];
        QUESTIONS.forEach((q, i) => {
          msgs.push({ type: 'bot', text: q.title });
          const ans = pending.answers[q.id];
          if (ans) {
            const display = Array.isArray(ans) ? ans.join(', ') : ans;
            msgs.push({ type: 'user', text: display });
            if (i < QUESTIONS.length - 1) {
              msgs.push({ type: 'bot', text: MICRO_RESPONSES[i % MICRO_RESPONSES.length] });
            }
          }
        });
        setMessages(msgs);
        // Restore results from stored career data
        setResults(pending.results.map(r => ({
          career: { slug: r.slug, name: r.name, short_description: r.short_description, category: r.category },
          score: 1,
          reasons: r.reasons,
        })));
        setCurrentQIdx(6);
        setPhase('results');
        setSaved(false);
        setSavePhase(null);
        clearPendingBuddyState();
        return;
      }
    }

    // Fresh start
    setMessages([{ type: 'bot', text: "Hi! I'll ask you a few quick questions to help you discover careers that may suit you." }]);
    setCurrentQIdx(-1);
    setAnswers({});
    setPendingAnswer(null);
    setPhase('chat');
    setResults([]);
    setSaved(false);
    setSavePhase(null);
    setSelectedChildId('');
  }, [open, restoreMode]);

  useEffect(() => { scrollToBottom(); }, [messages, phase, scrollToBottom]);

  // Start quiz
  const handleStart = () => {
    const q = QUESTIONS[0];
    setMessages(prev => [...prev, { type: 'bot', text: q.title, questionId: q.id }]);
    setCurrentQIdx(0);
    setPendingAnswer(null);
    scrollToBottom();
  };

  // Confirm answer and proceed
  const handleConfirmAnswer = () => {
    const q = QUESTIONS[currentQIdx];
    const answer = pendingAnswer;
    const displayText = Array.isArray(answer) ? answer.join(', ') : answer;

    // Store answer
    const newAnswers = { ...answers, [q.id]: answer };
    setAnswers(newAnswers);

    // Add user bubble
    const newMsgs = [...messages, { type: 'user', text: displayText }];

    if (currentQIdx < QUESTIONS.length - 1) {
      // Add micro-response + next question
      const micro = MICRO_RESPONSES[currentQIdx % MICRO_RESPONSES.length];
      const nextQ = QUESTIONS[currentQIdx + 1];
      newMsgs.push({ type: 'bot', text: micro });
      newMsgs.push({ type: 'bot', text: nextQ.title, questionId: nextQ.id });
      setMessages(newMsgs);
      setCurrentQIdx(currentQIdx + 1);
      setPendingAnswer(null);
    } else {
      // All questions answered — run matching
      newMsgs.push({ type: 'bot', text: "Let me find your best matches..." });
      setMessages(newMsgs);
      setCurrentQIdx(6);
      setTimeout(() => {
        runMatch(newAnswers);
      }, 600);
    }
    scrollToBottom();
  };

  const runMatch = (finalAnswers) => {
    const scored = allCareers
      .map(c => scoreCareer(c, finalAnswers))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
    setResults(scored);
    setPhase('results');
    scrollToBottom();
  };

  const handleReset = () => {
    setMessages([{ type: 'bot', text: "Hi! I'll ask you a few quick questions to help you discover careers that may suit you." }]);
    setCurrentQIdx(-1);
    setAnswers({});
    setPendingAnswer(null);
    setPhase('chat');
    setResults([]);
    setSaved(false);
    setSavePhase(null);
  };

  const handleViewCareer = (career) => {
    if (career === null) {
      const slugs = results.map(r => r.career.slug);
      onMatchComplete(slugs, results);
      onOpenChange(false);
    } else {
      const slugs = results.map(r => r.career.slug);
      onMatchComplete(slugs, results, career.slug);
      onOpenChange(false);
    }
  };

  // Save flow
  const handleSave = async () => {
    if (!user) {
      // Save state to sessionStorage and redirect to login
      savePendingState(answers, results);
      onOpenChange(false);
      navigate('/login?returnTo=careerBuddy');
      return;
    }
    if (user.role !== 'parent') {
      toast.error('Only parent accounts can save results');
      return;
    }
    try {
      const res = await api.get('/children');
      const kids = res.data || [];
      if (kids.length === 0) {
        setSavePhase('no-children');
        return;
      }
      if (kids.length === 1) {
        await doSave(kids[0].id);
      } else {
        setChildren(kids);
        setSavePhase('pick-child');
      }
    } catch {
      toast.error('Failed to load children');
    }
  };

  const doSave = async (childId) => {
    setSaving(true);
    try {
      const matchedCareers = results.map(r => ({
        slug: r.career.slug,
        name: r.career.name,
        short_description: r.career.short_description,
        reasons: r.reasons,
      }));
      await api.post('/career-buddy/save', { child_id: childId, answers, matched_careers: matchedCareers });
      setSaved(true);
      setSavePhase(null);
      toast.success("Saved to your child's dashboard!");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  // Render active question (the one currently being answered)
  const activeQ = currentQIdx >= 0 && currentQIdx < QUESTIONS.length ? QUESTIONS[currentQIdx] : null;
  const showIntroButton = currentQIdx === -1;
  const showActiveQ = activeQ && phase === 'chat' && currentQIdx < 6;
  const isMatching = currentQIdx === 6 && phase === 'chat';

  return (
    <Dialog open={open} onOpenChange={() => onOpenChange(false)}>
      <DialogContent className="rounded-2xl max-w-md max-h-[90vh] overflow-hidden p-0" data-testid="career-buddy-modal">
        <DialogTitle className="sr-only">Career Buddy</DialogTitle>
        <DialogDescription className="sr-only">Guided career discovery tool</DialogDescription>

        {/* Header */}
        <div className="flex items-center gap-2.5 px-4 pt-4 pb-2.5 border-b border-gray-100">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#E85D4A] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit']">Career Buddy</h2>
            <p className="text-[10px] text-[#9CA3AF]">Your career discovery guide</p>
          </div>
        </div>

        {/* Chat Area */}
        <div
          ref={scrollRef}
          className="px-4 py-3 overflow-y-auto"
          style={{ maxHeight: 'calc(90vh - 60px)' }}
        >
          {/* Save prompts overlay */}
          {savePhase === 'login' && (
            <SavePrompt type="login" onLogin={() => { savePendingState(answers, results); onOpenChange(false); navigate('/login?returnTo=careerBuddy'); }} onClose={() => setSavePhase(null)} />
          )}
          {savePhase === 'no-children' && (
            <SavePrompt type="no-children" onLogin={() => { onOpenChange(false); navigate('/parent'); }} onClose={() => setSavePhase(null)} />
          )}
          {savePhase === 'pick-child' && (
            <ChildPickerPrompt
              children={children}
              selectedChildId={selectedChildId}
              onSelect={setSelectedChildId}
              onConfirm={() => doSave(selectedChildId)}
              onClose={() => setSavePhase(null)}
              saving={saving}
            />
          )}

          {/* Chat messages */}
          {!savePhase && (
            <>
              {/* Message history */}
              {messages.map((msg, i) => {
                if (msg.type === 'bot') {
                  return (
                    <BotBubble key={i} animate={i === messages.length - 1 || i >= messages.length - 2}>
                      <p className="text-[13px] text-[#374151] leading-relaxed">{msg.text}</p>
                    </BotBubble>
                  );
                }
                return (
                  <UserBubble key={i} animate={i === messages.length - 1}>
                    <p className="text-[13px] leading-relaxed">{msg.text}</p>
                  </UserBubble>
                );
              })}

              {/* Loading spinner */}
              {loading && (
                <div className="py-6 text-center">
                  <div className="w-6 h-6 border-2 border-[#F97D6A] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-[11px] text-[#9CA3AF]">Loading careers...</p>
                </div>
              )}

              {/* Intro "Let's go" */}
              {showIntroButton && !loading && (
                <div className="pl-8 mb-3">
                  <div className="flex items-center gap-2 text-[10px] text-[#9CA3AF] mb-2">
                    <span>6 questions</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span>~2 min</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span>No login needed</span>
                  </div>
                  <Button
                    onClick={handleStart}
                    className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-9 px-6 text-xs font-semibold gap-1.5"
                    data-testid="career-buddy-start-btn"
                  >
                    Let's go <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              )}

              {/* Active question chips */}
              {showActiveQ && !loading && (
                <ActiveQuestion
                  question={activeQ}
                  stepIndex={currentQIdx}
                  totalSteps={QUESTIONS.length}
                  pendingAnswer={pendingAnswer}
                  onPendingChange={setPendingAnswer}
                  onConfirm={handleConfirmAnswer}
                />
              )}

              {/* Matching spinner */}
              {isMatching && (
                <div className="pl-8 py-4 flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[#F97D6A] border-t-transparent rounded-full animate-spin" />
                  <span className="text-[11px] text-[#9CA3AF]">Finding your matches...</span>
                </div>
              )}

              {/* Results */}
              {phase === 'results' && (
                <ResultsSection
                  results={results}
                  onViewCareer={handleViewCareer}
                  onSave={handleSave}
                  onReset={handleReset}
                  saving={saving}
                  saved={saved}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
